/**
 * Pre-order cart validation.
 *
 * When a pre-order product is added to the cart it carries an `Arriving`
 * line item property (set from the product's release_date / theme.label) so
 * the order is handled and billed as a pre-order: the B2B draft-order
 * splitter looks for a custom attribute whose key is exactly `Arriving`.
 *
 * PRE-ORDER TAG SEMANTICS (keep in sync with sections/main-product.liquid):
 *   preview        - not yet released anywhere; wholesale pre-order.
 *   retail-preview - already live for retail (so it ALSO carries `released`)
 *                    but still a wholesale pre-order.
 *   released       - live for retail. It does NOT mean at-once for wholesale.
 * A product stops being a wholesale pre-order only when its preview /
 * retail-preview tag is REMOVED. Do not treat `released` as an override:
 * that wrongly stripped Arriving from retail-preview items still on
 * Coming Soon. Once the preview tags are gone, any `Arriving` property on
 * the product's cart lines is stale and would route the line as a pre-order.
 *
 * This script re-checks cart lines against LIVE product tags and rewrites
 * stale lines. Shopify's Ajax Cart API cannot clear a line's properties to
 * an empty object, so `Arriving` is REPLACED by a private `_prebooked`
 * property (hidden from the customer at checkout) that records the original
 * month for ops. The splitter ignores any key other than `Arriving`, so the
 * line is treated as at-once.
 *
 * Runs when the cart page loads, when the cart drawer opens, after every
 * cart change, and again on the checkout click. The checkout click waits
 * for the cleanse to finish (with a generous cap) rather than racing it.
 * Accelerated/dynamic payment buttons (e.g. Shop Pay) bypass the checkout
 * click, which is why the cart is cleaned ahead of time as well.
 */
(function () {
  if (window.__preorderCheckoutValidationInit) return;
  window.__preorderCheckoutValidationInit = true;

  var ARRIVING_KEY = 'Arriving';
  var PREBOOKED_KEY = '_prebooked';
  var PREORDER_TAGS = ['preview', 'retail-preview'];
  var CHECKOUT_MAX_WAIT_MS = 20000;

  var productCache = {};
  var inFlight = null;
  var rerunRequested = false;
  var ignoreOwnEvent = false;

  function fetchJSON(url, options) {
    return fetch(url, options || { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; });
  }

  function getProduct(handle) {
    if (!productCache[handle]) {
      productCache[handle] = fetchJSON('/products/' + handle + '.js')
        .catch(function () { return null; })
        .then(function (product) {
          // Don't cache failures so a later run can retry.
          if (!product) delete productCache[handle];
          return product;
        });
    }
    return productCache[handle];
  }

  function normaliseTags(product) {
    if (!product || !Array.isArray(product.tags)) return null;
    return product.tags.map(function (tag) {
      return String(tag).toLowerCase().trim();
    });
  }

  // A line is still a pre-order while its product carries `preview` or
  // `retail-preview`. `released` is deliberately NOT consulted (see header).
  // Unknown product (fetch failed) -> assume still a pre-order so we never
  // strip a property we're unsure of.
  function isStillPreorder(product) {
    var tags = normaliseTags(product);
    if (!tags) return true;
    return tags.some(function (tag) {
      return PREORDER_TAGS.indexOf(tag) !== -1;
    });
  }

  // "Arriving September 2026" -> "September 2026"
  function stripArrivingPrefix(value) {
    return String(value || '').replace(/^\s*Arriving\s+/i, '').trim();
  }

  function buildReplacementProperties(item) {
    var properties = {};
    Object.keys(item.properties).forEach(function (key) {
      if (key !== ARRIVING_KEY) properties[key] = item.properties[key];
    });
    // Shopify ignores a change to an empty properties object, so always
    // leave a (private, hidden) marker behind.
    properties[PREBOOKED_KEY] = stripArrivingPrefix(item.properties[ARRIVING_KEY]) || 'yes';
    return properties;
  }

  function changeLine(change) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(change)
    }).then(function (r) { return r.ok; }).catch(function () { return false; });
  }

  // Inspects the cart and rewrites stale `Arriving` lines. Resolves to the
  // number of lines that were changed.
  function cleanseCart() {
    return fetchJSON('/cart.js').then(function (cart) {
      if (!cart || !cart.items) return 0;

      var candidates = cart.items.filter(function (item) {
        return item.properties && item.properties[ARRIVING_KEY];
      });
      if (!candidates.length) return 0;

      return Promise.all(candidates.map(function (item) {
        return getProduct(item.handle).then(function (product) {
          if (isStillPreorder(product)) return null;
          return {
            id: item.key,
            quantity: item.quantity,
            properties: buildReplacementProperties(item)
          };
        });
      })).then(function (changes) {
        // Apply sequentially: change.js works on one line at a time and
        // each call returns the new cart.
        return changes.filter(Boolean).reduce(function (chain, change) {
          return chain.then(function (count) {
            return changeLine(change).then(function (ok) {
              return ok ? count + 1 : count;
            });
          });
        }, Promise.resolve(0));
      });
    });
  }

  // Single-flight runner. If a run is requested while one is in progress,
  // one more run is queued after it finishes so no cart change is missed.
  // `silent` suppresses the theme refresh event (used right before we leave
  // the page for checkout, so the DOM isn't torn down under us).
  function runCleanse(silent) {
    if (inFlight) {
      rerunRequested = true;
      return inFlight;
    }
    inFlight = cleanseCart()
      .catch(function () { return 0; })
      .then(function (changed) {
        inFlight = null;
        if (changed > 0 && !silent) {
          // Let the theme re-render the drawer / cart page / header count.
          // Our own event must not re-enter the cleanse (guards against a
          // loop if Shopify ever accepts a change without applying it).
          ignoreOwnEvent = true;
          try {
            document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true, cancelable: false }));
          } finally {
            ignoreOwnEvent = false;
          }
        }
        if (rerunRequested) {
          rerunRequested = false;
          return runCleanse(silent).then(function (more) { return changed + more; });
        }
        return changed;
      });
    return inFlight;
  }

  function withTimeout(promise, ms) {
    return Promise.race([
      promise,
      new Promise(function (resolve) { setTimeout(resolve, ms); })
    ]);
  }

  // Runs validation then invokes proceed() exactly once, win or lose.
  function validateThen(proceed) {
    var done = false;
    function go() {
      if (done) return;
      done = true;
      proceed();
    }
    withTimeout(runCleanse(true).catch(function () {}), CHECKOUT_MAX_WAIT_MS).then(go, go);
  }

  function setBusy(el, busy) {
    if (!el) return;
    if (busy) {
      el.setAttribute('aria-busy', 'true');
      el.classList.add('is-loading');
      if (el.tagName === 'BUTTON') {
        el.dataset.preorderLabel = el.textContent;
        el.textContent = 'Updating cart…';
        el.disabled = true;
      }
    } else {
      el.removeAttribute('aria-busy');
      el.classList.remove('is-loading');
      if (el.tagName === 'BUTTON') {
        if (el.dataset.preorderLabel) el.textContent = el.dataset.preorderLabel;
        el.disabled = false;
      }
    }
  }

  /* ---------- Triggers ---------- */

  // 1. Cart page load.
  function onReady() {
    if (document.querySelector('.template-cart, cart-form')) runCleanse(false);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady);
  } else {
    onReady();
  }

  // 2. Cart drawer opens (covers every add-to-cart path, including the
  //    wholesale grid, wishlist and purchase-history drawers).
  function watchDrawer() {
    var drawer = document.querySelector('.js-cart-drawer');
    if (!drawer || drawer.__preorderWatched) return;
    drawer.__preorderWatched = true;
    new MutationObserver(function () {
      if (drawer.hasAttribute('open')) runCleanse(false);
    }).observe(drawer, { attributes: true, attributeFilter: ['open'] });
  }
  watchDrawer();
  document.addEventListener('DOMContentLoaded', watchDrawer);

  // 3. Any cart change the theme reports (adds, quantity edits, merges).
  //    The refresh event we dispatch ourselves is skipped.
  ['on:cart:change', 'on:cart:add', 'on:cart:after-merge'].forEach(function (name) {
    document.addEventListener(name, function () {
      if (ignoreOwnEvent) return;
      runCleanse(false);
    });
  });

  // 4. Checkout click: wait for a final cleanse before leaving the page.
  document.addEventListener('click', function (e) {
    // Standard checkout submit button (cart page).
    var checkoutBtn = e.target.closest('button[name="checkout"], input[name="checkout"]');
    if (checkoutBtn && !checkoutBtn.dataset.preorderValidated) {
      var form = checkoutBtn.form || checkoutBtn.closest('form');
      if (!form) return;
      e.preventDefault();
      setBusy(checkoutBtn, true);
      validateThen(function () {
        // The cart form may have re-rendered while we waited; submit
        // whichever checkout button is live now.
        var liveForm = document.contains(form) ? form : document.getElementById(form.id);
        if (!liveForm) { window.location.href = '/checkout'; return; }
        var liveBtn = document.contains(checkoutBtn)
          ? checkoutBtn
          : liveForm.querySelector('button[name="checkout"], input[name="checkout"]');
        setBusy(liveBtn, false);
        if (liveBtn) liveBtn.dataset.preorderValidated = 'true';
        if (typeof liveForm.requestSubmit === 'function' && liveBtn) {
          liveForm.requestSubmit(liveBtn);
        } else {
          liveForm.submit();
        }
      });
      return;
    }

    // Direct checkout link (cart drawer).
    var checkoutLink = e.target.closest('a[href*="/checkout"]');
    if (checkoutLink && !checkoutLink.dataset.preorderValidated) {
      var href = checkoutLink.href;
      e.preventDefault();
      setBusy(checkoutLink, true);
      validateThen(function () {
        checkoutLink.dataset.preorderValidated = 'true';
        window.location.href = href;
      });
    }
  }, true);
})();
